import { useEffect, useState, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import MobileModal from './MobileModal';
import { isLaunchEquipment, isReservableClass, tracksMeters } from '../constants/boatClasses';
import {
  RESERVATION_TIME_OPTIONS,
  DEFAULT_RESERVATION_START_TIME,
  DEFAULT_RESERVATION_END_TIME,
  formatReservationTimeLabel,
  canUserReserveBoat,
  getLocalISODate
} from '../utils/reservationTimes';
import { getSeatCountForBoatClass } from '../utils/boatSeats';
import { formatTimeOfDay } from '../utils/timeOfDay';
import TrainingBlockAnalytics from './TrainingBlockAnalytics';
import {
  groupPlanEntriesByWeek,
  contiguousBlockWeeks,
  weekStartForDate,
  formatMdy,
  mondayOf
} from '../utils/planAnalytics';
import { WorkoutTargetCard } from './KorzoTargetPanel';
import KorzoSelfProg from './KorzoSelfProg';
import SearchableMemberChecklist from './SearchableMemberChecklist';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const materialKindLabel = (kind) => {
  if (kind === 'video') return 'Video';
  if (kind === 'page') return 'Page';
  if (kind === 'pdf') return 'PDF';
  return 'Photo';
};

const downloadPlanMaterial = async (item) => {
  const base = (item.label || 'plan-material').replace(/[^\w.-]+/g, '_').slice(0, 80) || 'plan-material';
  const extMatch = String(item.url || '').split('?')[0].match(/\.([a-z0-9]+)$/i);
  const fallbackExt = item.kind === 'pdf' ? 'pdf' : 'jpg';
  const filename = base.includes('.') ? base : `${base}.${extMatch ? extMatch[1] : fallbackExt}`;
  try {
    const response = await fetch(item.url);
    if (!response.ok) throw new Error('download failed');
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(item.url, '_blank', 'noopener,noreferrer');
  }
};

export default function DailyWorkout() {
  const { teamSettings } = useTheme();
  const { user, isCoach, isAdmin } = useAuth();
  const targetTimesEnabled = Boolean(teamSettings?.targetTimesEnabled);
  const [sessions, setSessions] = useState([]);
  const [planSessions, setPlanSessions] = useState([]);
  const [planMaterials, setPlanMaterials] = useState([]);
  const [showPlanMaterials, setShowPlanMaterials] = useState(false);
  const [planEntries, setPlanEntries] = useState([]);
  const [planZoneLoggingEnabled, setPlanZoneLoggingEnabled] = useState(false);
  const [planZoneDefinitions, setPlanZoneDefinitions] = useState([]);
  const [mySquadId, setMySquadId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [allWorkouts, setAllWorkouts] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [athletes, setAthletes] = useState([]);
  const [athletesError, setAthletesError] = useState(false);
  const [weather, setWeather] = useState(null);
  const [water, setWater] = useState(null);
  
  // Sign-up state
  const [signupStatus, setSignupStatus] = useState({}); // { workoutId: { isSignedUp, signups, loading, spotsRemaining } }
  const [signingUp, setSigningUp] = useState(null); // workoutId currently being signed up for
  const [walkOnPickerSessionId, setWalkOnPickerSessionId] = useState(null); // workoutId whose coach picker is open
  const [walkOnSearch, setWalkOnSearch] = useState('');
  const [addingWalkOn, setAddingWalkOn] = useState(null); // { workoutId, athleteId } being added
  
  // Sign-up history state
  const [showSignupHistory, setShowSignupHistory] = useState(false);
  const [signupHistory, setSignupHistory] = useState([]);
  const [signupHistoryLoading, setSignupHistoryLoading] = useState(false);
  const [historyDateRange, setHistoryDateRange] = useState({ startDate: '', endDate: '' });

  const [quickBoats, setQuickBoats] = useState([]);
  const [showQuickReserve, setShowQuickReserve] = useState(false);
  const [showQuickLog, setShowQuickLog] = useState(false);
  const [qrBoatId, setQrBoatId] = useState('');
  const [qrDate, setQrDate] = useState('');
  const [qrStart, setQrStart] = useState(DEFAULT_RESERVATION_START_TIME);
  const [qrEnd, setQrEnd] = useState(DEFAULT_RESERVATION_END_TIME);
  const [qrPurpose, setQrPurpose] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');
  const [qrAssignedUserIds, setQrAssignedUserIds] = useState([]);
  const [quickTeamMembers, setQuickTeamMembers] = useState([]);

  const [qlBoatId, setQlBoatId] = useState('');
  const [qlDate, setQlDate] = useState('');
  const [qlMeters, setQlMeters] = useState('');
  const [qlNotes, setQlNotes] = useState('');
  const [qlLoading, setQlLoading] = useState(false);
  const [qlError, setQlError] = useState('');
  const [qlAssignedUserIds, setQlAssignedUserIds] = useState([]);

  function formatDateMMDDYYYY(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  useEffect(() => {
    fetchAllWorkouts();
    fetchAttendance();
    fetchAthletes();
    loadTodaysWorkout();
  }, []);

  useEffect(() => {
    if (user?.team) {
      fetchPlanSchedule();
    } else {
      setPlanEntries([]);
    }
  }, [user?.team]);

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
        setAthletesError(false);
      } else {
        setAthletesError(true);
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
      setAthletesError(true);
    }
  };

  // Parse weight string to pounds (handles various formats)
  const parseWeight = (weightStr) => {
    if (!weightStr) return null;
    // Extract numbers from strings like "190 lbs", "190", "190lbs"
    const match = weightStr.toString().match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : null;
  };

  // Parse erg score to seconds (handles formats like "6:10", "6:10.5", "370")
  const parseErgScore = (ergStr) => {
    if (!ergStr) return null;
    const str = ergStr.toString().trim();
    // Handle MM:SS or MM:SS.s format
    const timeMatch = str.match(/^(\d+):(\d+(?:\.\d+)?)$/);
    if (timeMatch) {
      return parseInt(timeMatch[1]) * 60 + parseFloat(timeMatch[2]);
    }
    // Handle plain seconds
    const seconds = parseFloat(str);
    return isNaN(seconds) ? null : seconds;
  };

  // Format seconds back to MM:SS.s
  const formatErgScore = (totalSeconds) => {
    if (totalSeconds === null) return 'N/A';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = (totalSeconds % 60).toFixed(1);
    return `${minutes}:${seconds.padStart(4, '0')}`;
  };

  // Calculate averages for a boat's crew (excluding coxswains)
  // Supports both new format (seats array) and old format (athletes array + coxswains)
  const calculateCrewAverages = (boat) => {
    let rowersOnly = [];
    let coxCount = 0;
    
    if (boat.seats && Array.isArray(boat.seats)) {
      // New format: seats array with { seat, athlete }
      rowersOnly = boat.seats
        .filter(s => s.seat !== 'Cox' && s.athlete)
        .map(s => s.athlete);
      coxCount = boat.seats.filter(s => s.seat === 'Cox' && s.athlete).length;
    } else {
      // Old format: athletes array + coxswains
      const boatAthletes = boat.athletes || [];
      const coxswains = boat.coxswains || [];
      rowersOnly = boatAthletes
        .map((name, index) => ({ name, index }))
        .filter(({ index }) => !coxswains.includes(index))
        .map(({ name }) => name);
      coxCount = coxswains.length;
    }
    
    const crewData = rowersOnly
      .map(name => athletes.find(a => a.name === name))
      .filter(a => a);
    
    // Calculate average weight
    const weights = crewData.map(a => parseWeight(a.weight)).filter(w => w !== null);
    const avgWeight = weights.length > 0 ? weights.reduce((a, b) => a + b, 0) / weights.length : null;
    
    // Calculate average erg score (handle both erg_score and ergScore field names)
    const ergScores = crewData.map(a => parseErgScore(a.erg_score || a.ergScore)).filter(e => e !== null);
    const avgErg = ergScores.length > 0 ? ergScores.reduce((a, b) => a + b, 0) / ergScores.length : null;
    
    return {
      avgWeight: avgWeight !== null ? Math.round(avgWeight) : null,
      avgErg: avgErg !== null ? formatErgScore(avgErg) : null,
      weightCount: weights.length,
      ergCount: ergScores.length,
      totalCount: rowersOnly.filter(a => a).length,
      coxCount
    };
  };

  // Get seat label style
  const getSeatLabelStyle = (seat) => {
    if (seat === 'Cox') return 'bg-purple-100 text-purple-700';
    if (seat === 'Stroke') return 'bg-blue-100 text-blue-700';
    if (seat === 'Bow') return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-600';
  };

  const fetchAllWorkouts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/workouts`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAllWorkouts(data.sort((a, b) => new Date(a.date) - new Date(b.date)));
      } else {
        console.error('Failed to fetch workouts');
      }
    } catch (error) {
      console.error('Error fetching workouts:', error);
    }
  };

  const fetchPlanSchedule = async () => {
    if (!user?.team) return;
    try {
      const token = localStorage.getItem('token');
      const start = new Date();
      start.setMonth(start.getMonth() - 6);
      const end = new Date();
      end.setMonth(end.getMonth() + 6);
      const params = new URLSearchParams({
        startDate: formatDateMMDDYYYY(start),
        endDate: formatDateMMDDYYYY(end)
      });
      const response = await fetch(`${API_URL}/api/training-plans/schedule?${params}`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setPlanEntries(data.enabled ? (data.entries || []) : []);
        setPlanZoneLoggingEnabled(Boolean(data.zoneLoggingEnabled));
        setPlanZoneDefinitions(Array.isArray(data.zoneDefinitions) ? data.zoneDefinitions : []);
      } else {
        setPlanEntries([]);
        setPlanZoneLoggingEnabled(false);
        setPlanZoneDefinitions([]);
      }
    } catch (error) {
      console.error('Error fetching training plan schedule:', error);
      setPlanEntries([]);
      setPlanZoneLoggingEnabled(false);
      setPlanZoneDefinitions([]);
    }
  };

  const fetchAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/attendance`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        const absenceMap = {};
        data.forEach((record) => {
          if (!absenceMap[record.athlete_name]) {
            absenceMap[record.athlete_name] = [];
          }
          absenceMap[record.athlete_name].push(record.absence_date);
        });
        setAttendanceRecords(absenceMap);
      } else {
        console.error('Failed to fetch attendance');
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const loadTodaysWorkout = async () => {
    const today = new Date();
    const todayKey = formatDateMMDDYYYY(today);
    await loadWorkoutByDate(todayKey);
  };

  const getWorkoutTeamQuery = () => {
    if (user?.team) {
      return `?team=${encodeURIComponent(user.team)}`;
    }
    return '';
  };

  const loadWorkoutByDate = async (date) => {
    setShowPlanMaterials(false);
    try {
      const token = localStorage.getItem('token');
      const encodedDate = encodeURIComponent(date);
      const workoutPromise = fetch(`${API_URL}/api/workouts/${encodedDate}${getWorkoutTeamQuery()}`, {
        headers: getAuthHeaders(token)
      });
      const planPromise = user?.team
        ? fetch(`${API_URL}/api/training-plans/for-date?date=${encodedDate}`, {
          headers: getAuthHeaders(token)
        })
        : Promise.resolve(null);

      const [response, planRes] = await Promise.all([workoutPromise, planPromise]);

      if (planRes?.ok) {
        const planData = await planRes.json();
        setPlanSessions(planData.enabled ? (planData.entries || []) : []);
        setPlanMaterials(planData.enabled ? (planData.materials || []) : []);
        setMySquadId(planData.callerSquadId ?? null);
      } else {
        setPlanSessions([]);
        setPlanMaterials([]);
      }

      if (response.ok) {
        const sessionsData = await response.json();
        const sessionsArray = Array.isArray(sessionsData) ? sessionsData : [sessionsData];
        setSessions(sessionsArray);
        setSelectedDate(date);
        fetchConditions(date);

        sessionsArray.forEach(session => {
          if (session.signup_enabled && session.id) {
            fetchSignupStatus(session.id);
          }
        });
      } else {
        setSessions([]);
        setSelectedDate(date);
        fetchConditions(date);
      }
    } catch (error) {
      console.error('Error loading workout:', error);
      setSessions([]);
      setPlanSessions([]);
      setPlanMaterials([]);
      setWeather(null);
      setWater(null);
    }
  };

  // Fetch signup status for a workout
  const fetchSignupStatus = async (workoutId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch signups list
      const signupsResponse = await fetch(`${API_URL}/api/workouts/signup/${workoutId}`, {
        headers: getAuthHeaders(token)
      });
      
      // Fetch my signup status
      const myStatusResponse = await fetch(`${API_URL}/api/workouts/signup/${workoutId}/my-status`, {
        headers: getAuthHeaders(token)
      });
      
      if (signupsResponse.ok && myStatusResponse.ok) {
        const signupsData = await signupsResponse.json();
        const myStatusData = await myStatusResponse.json();
        
        setSignupStatus(prev => ({
          ...prev,
          [workoutId]: {
            signups: signupsData.signups || [],
            signupLimit: signupsData.signupLimit,
            spotsRemaining: signupsData.spotsRemaining,
            isSignedUp: myStatusData.isSignedUp,
            myAthleteId: myStatusData.athleteId,
            loading: false
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching signup status:', error);
    }
  };

  // Sign up for a workout
  const handleSignUp = async (workoutId) => {
    setSigningUp(workoutId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/workouts/signup/${workoutId}`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        // Refresh signup status
        await fetchSignupStatus(workoutId);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to sign up');
      }
    } catch (error) {
      console.error('Error signing up:', error);
      alert('Error signing up. Please try again.');
    } finally {
      setSigningUp(null);
    }
  };

  // Cancel signup
  const handleCancelSignup = async (workoutId) => {
    if (!confirm('Are you sure you want to cancel your signup?')) return;
    
    setSigningUp(workoutId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/workouts/signup/${workoutId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      
      if (response.ok) {
        await fetchSignupStatus(workoutId);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to cancel signup');
      }
    } catch (error) {
      console.error('Error canceling signup:', error);
      alert('Error canceling signup. Please try again.');
    } finally {
      setSigningUp(null);
    }
  };

  /** Club athletes on this session's team. A session with no team is open to the whole club. */
  const getSessionRoster = (session) => {
    const sessionTeam = session.team ? session.team.trim().toLowerCase() : null;
    if (!sessionTeam) return athletes;

    return athletes.filter((athlete) => {
      const athleteTeams = [athlete.team, ...(athlete.teams || [])];
      return athleteTeams.some((team) => team && team.trim().toLowerCase() === sessionTeam);
    });
  };

  /** Roster options for the coach walk-on picker: on the session's team and not already signed up. */
  const getAthletesAvailableToAdd = (session) => {
    const signedUpIds = new Set((signupStatus[session.id]?.signups || []).map((s) => String(s.athlete_id)));
    return getSessionRoster(session).filter((athlete) => !signedUpIds.has(String(athlete.id)));
  };

  /** An empty picker has several very different causes; say which one it is. */
  const getWalkOnEmptyMessage = (session) => {
    if (athletesError) return 'Could not load the roster. Refresh the page and try again.';
    if (athletes.length === 0) return 'No athletes on this club roster yet.';
    if (getSessionRoster(session).length === 0) {
      return `No athletes are assigned to ${session.team}, so there is nobody to add.`;
    }
    return 'Everyone on the roster is already signed up.';
  };

  /** Narrow the walk-on options by the coach's search text. */
  const filterWalkOnSearch = (athletes) => {
    const query = walkOnSearch.trim().toLowerCase();
    if (!query) return athletes;
    return athletes.filter((athlete) => athlete.name.toLowerCase().includes(query));
  };

  const toggleWalkOnPicker = (workoutId) => {
    setWalkOnSearch('');
    setWalkOnPickerSessionId((prev) => (prev === workoutId ? null : workoutId));
  };

  // Coach: Sign up an athlete who showed up without signing themselves up
  const handleAddAthleteSignup = async (workoutId, athlete) => {
    setAddingWalkOn({ workoutId, athleteId: athlete.id });
    try {
      const token = localStorage.getItem('token');
      const postSignup = (overrideLimit) =>
        fetch(`${API_URL}/api/workouts/signup/${workoutId}/athlete/${athlete.id}`, {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify(overrideLimit ? { overrideLimit: true } : {})
        });

      let response = await postSignup(false);

      // A full session isn't a hard stop for a coach — the athlete is already here.
      if (response.status === 409) {
        const conflict = await response.json();
        if (conflict.code !== 'SIGNUP_LIMIT_REACHED') {
          alert(conflict.error || 'Failed to add athlete');
          return;
        }
        if (!confirm(`${conflict.error}. Add ${athlete.name} anyway?`)) return;
        response = await postSignup(true);
      }

      if (response.ok) {
        setWalkOnPickerSessionId(null);
        setWalkOnSearch('');
        await fetchSignupStatus(workoutId);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add athlete');
      }
    } catch (error) {
      console.error('Error adding athlete to signup:', error);
      alert('Error adding athlete. Please try again.');
    } finally {
      setAddingWalkOn(null);
    }
  };

  // Coach: Remove an athlete's signup
  const handleRemoveAthleteSignup = async (workoutId, athleteId, athleteName) => {
    if (!confirm(`Remove ${athleteName} from this workout?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/workouts/signup/${workoutId}/athlete/${athleteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      
      if (response.ok) {
        await fetchSignupStatus(workoutId);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to remove athlete');
      }
    } catch (error) {
      console.error('Error removing athlete:', error);
      alert('Error removing athlete. Please try again.');
    }
  };

  // Fetch user's signup history
  const fetchSignupHistory = async (startDate = '', endDate = '') => {
    setSignupHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/workouts/signups/my-history`;
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setSignupHistory(data.signups || []);
      }
    } catch (error) {
      console.error('Error fetching signup history:', error);
    } finally {
      setSignupHistoryLoading(false);
    }
  };

  // Absences for the selected date (from API — already team-scoped for most roles)
  const rawAbsentForDate = useMemo(() => {
    if (!selectedDate) return [];
    return Object.entries(attendanceRecords)
      .filter(([, dates]) => dates.includes(selectedDate))
      .map(([athlete]) => athlete);
  }, [selectedDate, attendanceRecords]);

  // Enforce same-team visibility on the daily workout page (athletes + team-assigned coaches)
  const absentAthletes = useMemo(() => {
    if (rawAbsentForDate.length === 0) return [];
    if (!user) return [];
    if (user.role === 'admin' || user.role === 'superadmin') return rawAbsentForDate;
    if (user.role === 'coach' && !user.team) return rawAbsentForDate;
    if (user.role === 'coach' && user.team) {
      return rawAbsentForDate.filter((name) => {
        const a = athletes.find((at) => at.name === name);
        return a && a.team === user.team;
      });
    }
    if (user.team) {
      return rawAbsentForDate.filter((name) => {
        const a = athletes.find((at) => at.name === name);
        return a && a.team === user.team;
      });
    }
    return rawAbsentForDate.filter((name) => name === user.name);
  }, [rawAbsentForDate, user, athletes]);

  const fetchConditions = async (date) => {
    try {
      const token = localStorage.getItem('token');
      const [month, day, year] = date.split('/').map(Number);
      const isoDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      const response = await fetch(
        `${API_URL}/api/conditions?date=${encodeURIComponent(isoDate)}`,
        { headers: getAuthHeaders(token) }
      );
      if (!response.ok) {
        setWeather(null);
        setWater(null);
        return;
      }

      const data = await response.json();
      if (data.weather) {
        setWeather({
          temp: data.weather.tempF,
          feelsLike: data.weather.feelsLikeF,
          description: data.weather.description,
          emoji: mapWeatherToEmoji(data.weather.main),
          windSpeed: data.weather.windSpeedMph,
          windDirection: data.weather.windDirection,
          kind: data.weather.kind
        });
      } else {
        setWeather(null);
      }
      setWater(data.water || null);
    } catch (error) {
      console.error('Conditions fetch failed:', error);
      setWeather(null);
      setWater(null);
    }
  };

  const mapWeatherToEmoji = (main) => {
    switch (main) {
      case 'Clear':
        return '☀️';
      case 'Clouds':
        return '☁️';
      case 'Rain':
        return '🌧️';
      case 'Thunderstorm':
        return '⛈️';
      case 'Snow':
        return '❄️';
      default:
        return '🌡️';
    }
  };

  const today = formatDateMMDDYYYY(new Date());
  const todayDateObj = new Date();
  
  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Helper to parse MM/DD/YYYY to Date object for proper comparison
  const parseDateMMDDYYYY = (dateStr) => {
    const [month, day, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  };
  
  // Session counts for calendar badges (workouts vs planned training volume).
  const getWorkoutCount = (dateStr) => allWorkouts.filter((w) => w.date === dateStr).length;
  const getPlanCount = (dateStr) => planEntries.filter((e) => e.date === dateStr).length;
  const getCalendarBadge = (dateStr) => {
    const workoutCount = getWorkoutCount(dateStr);
    if (workoutCount > 0) return { count: workoutCount, kind: 'workout' };
    const planCount = getPlanCount(dateStr);
    if (planCount > 0) return { count: planCount, kind: 'plan' };
    return null;
  };

  const formatPlanZones = (zones) => {
    if (!zones || typeof zones !== 'object') return '';
    const parts = Object.entries(zones)
      .filter(([, v]) => parseInt(v, 10) > 0)
      .map(([k, v]) => `${k} ${v}'`);
    return parts.length ? ` (${parts.join(', ')})` : '';
  };
  
  // Get calendar days for the current month view
  const getCalendarDays = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Start from Sunday of the first week
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    // End on Saturday of the last week
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };
  
  const navigateMonth = (direction) => {
    setCalendarDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };
  
  const goToToday = () => {
    setCalendarDate(new Date());
    loadWorkoutByDate(today);
  };

  const focusWeekStart = weekStartForDate(selectedDate || today) || formatMdy(mondayOf(new Date()));
  const trainingBlockWeeks = useMemo(() => {
    const allWeeks = groupPlanEntriesByWeek(planEntries, planZoneDefinitions);
    return contiguousBlockWeeks(allWeeks, focusWeekStart);
  }, [planEntries, planZoneDefinitions, focusWeekStart]);

  const getSessionLabel = (sessionNumber, totalSessions) => {
    if (totalSessions === 1) return '';
    if (sessionNumber === 1) return 'Morning';
    if (sessionNumber === 2) return 'Afternoon';
    if (sessionNumber === 3) return 'Evening';
    return `Session ${sessionNumber}`;
  };

  const inputDateFromSelected = () => {
    if (selectedDate) {
      const parts = selectedDate.split('/');
      if (parts.length === 3) {
        const [mo, d, y] = parts;
        return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
    }
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  };

  const fetchQuickBoats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/boats`, { headers: getAuthHeaders(token) });
      if (response.ok) {
        const data = await response.json();
        setQuickBoats(data.boats || []);
      }
    } catch (e) {
      console.error('Failed to load boats', e);
    }
  };

  const reservableQuickBoats = useMemo(() => {
    if (!user) return [];
    return quickBoats
      .filter((b) => isReservableClass(b.boat_class) && canUserReserveBoat(b, user.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [quickBoats, user]);

  const loggableQuickBoats = useMemo(() => {
    return quickBoats
      .filter((b) => tracksMeters(b.boat_class))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [quickBoats]);

  const qrSelectedBoat = useMemo(
    () => reservableQuickBoats.find((b) => String(b.id) === String(qrBoatId)),
    [reservableQuickBoats, qrBoatId]
  );
  const qrCrewCap = qrSelectedBoat ? getSeatCountForBoatClass(qrSelectedBoat.boat_class) : 0;
  const qlSelectedBoat = useMemo(
    () => loggableQuickBoats.find((b) => String(b.id) === String(qlBoatId)),
    [loggableQuickBoats, qlBoatId]
  );
  const qlCrewCap = qlSelectedBoat ? getSeatCountForBoatClass(qlSelectedBoat.boat_class) : 0;

  const fetchQuickTeamMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/boats/team-members`, {
        headers: getAuthHeaders(token)
      });
      if (res.ok) {
        const data = await res.json();
        setQuickTeamMembers(data.members || []);
      }
    } catch (e) {
      console.error('Failed to load team members', e);
    }
  };

  const openQuickReserve = () => {
    setQrError('');
    setQrAssignedUserIds([]);
    let next = inputDateFromSelected();
    const today = getLocalISODate();
    if (next < today) next = today;
    setQrDate(next);
    setQrStart(DEFAULT_RESERVATION_START_TIME);
    setQrEnd(DEFAULT_RESERVATION_END_TIME);
    setQrBoatId('');
    setQrPurpose('');
    setShowQuickReserve(true);
    fetchQuickBoats();
    fetchQuickTeamMembers();
  };

  const openQuickLog = () => {
    setQlError('');
    setQlAssignedUserIds([]);
    let next = inputDateFromSelected();
    const today = getLocalISODate();
    if (next > today) next = today;
    setQlDate(next);
    setQlBoatId('');
    setQlMeters('');
    setQlNotes('');
    setShowQuickLog(true);
    fetchQuickBoats();
    fetchQuickTeamMembers();
  };

  const handleQuickReserve = async (e) => {
    e.preventDefault();
    setQrError('');
    if (!qrBoatId || !qrDate || !qrStart || !qrEnd) {
      setQrError('Select a boat, date, and start/end times.');
      return;
    }
    const todayIso = getLocalISODate();
    if (qrDate < todayIso) {
      setQrError('Reservations are not available for past dates.');
      return;
    }
    setQrLoading(true);
    try {
      const [y, m, d] = qrDate.split('-');
      const reservationDate = `${m}/${d}/${y}`;
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/boats/reservations`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          boatId: parseInt(qrBoatId, 10),
          reservationDate,
          startTime: qrStart,
          endTime: qrEnd,
          purpose: qrPurpose || null,
          ...(qrAssignedUserIds.length > 0 && {
            assignedUserIds: qrAssignedUserIds.map((id) => parseInt(id, 10))
          })
        })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setShowQuickReserve(false);
        alert('Reservation created.');
      } else {
        setQrError(data.error || 'Failed to create reservation');
      }
    } catch (err) {
      setQrError('Failed to create reservation');
    } finally {
      setQrLoading(false);
    }
  };

  const handleQuickLog = async (e) => {
    e.preventDefault();
    setQlError('');
    const meters = parseInt(qlMeters, 10);
    if (!qlBoatId || !qlDate || !meters || meters <= 0) {
      setQlError('Select a boat, date, and a valid number of meters.');
      return;
    }
    const todayIso = getLocalISODate();
    if (qlDate > todayIso) {
      setQlError('Meters can only be logged for today or earlier, not for future dates.');
      return;
    }
    const [y, m, d] = qlDate.split('-');
    const logDate = `${m}/${d}/${y}`;
    const token = localStorage.getItem('token');
    setQlLoading(true);
    const postMeters = async (skipBoatTotal, acknowledgeAssigneeOverlap) => {
      const body = {
        boatId: parseInt(qlBoatId, 10),
        meters,
        date: logDate,
        notes: qlNotes || null,
        skipBoatTotal,
        ...(qlAssignedUserIds.length > 0 && {
          assignedUserIds: qlAssignedUserIds.map((id) => parseInt(id, 10))
        }),
        ...(acknowledgeAssigneeOverlap && { acknowledgeAssigneeOverlap: true })
      };
      const postRes = await fetch(`${API_URL}/api/boats/meters`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(body)
      });
      const postData = await postRes.json().catch(() => ({}));
      if (postRes.status === 409 && postData.code === 'ASSIGNEE_OVERLAP' && postData.assigneeOverlap?.length) {
        const detail = postData.assigneeOverlap
          .map(
            (row) =>
              `${row.name}: ${row.entries.map((e) => `${e.meters}m (${e.source})`).join(', ')}`
          )
          .join('\n');
        if (
          window.confirm(
            `Some selected athletes already have personal meters for this date:\n\n${detail}\n\nOK to add this credit anyway?`
          )
        ) {
          return postMeters(skipBoatTotal, true);
        }
        return false;
      }
      if (postRes.ok) {
        setShowQuickLog(false);
        setQlAssignedUserIds([]);
        alert('Meters logged.');
        return true;
      }
      setQlError(postData.error || 'Failed to log meters');
      return false;
    };
    try {
      const checkRes1 = await fetch(
        `${API_URL}/api/boats/meters/check?boatId=${qlBoatId}&date=${encodeURIComponent(logDate)}`,
        { headers: getAuthHeaders(token) }
      );
      const checkData1 = await checkRes1.json();
      let skipBoatTotal = false;
      if (checkData1.hasExisting) {
        const sameSession = window.confirm(
          'There is already a meters entry for this boat on this date. Click OK if this is the same outing (meters are logged but the boat total is not increased again), or Cancel if this is a different outing (full log).'
        );
        skipBoatTotal = sameSession;
      }

      const params2 = new URLSearchParams({ boatId: String(qlBoatId), date: logDate });
      if (qlAssignedUserIds.length > 0) {
        params2.set('userIds', qlAssignedUserIds.join(','));
      }
      const checkRes2 = await fetch(`${API_URL}/api/boats/meters/check?${params2.toString()}`, {
        headers: getAuthHeaders(token)
      });
      const checkData2 = await checkRes2.json();
      if (!checkRes2.ok) {
        setQlError(checkData2.error || 'Failed to check personal meters for the crew');
        return;
      }
      let needAck = false;
      if (Array.isArray(checkData2.assigneeOverlap) && checkData2.assigneeOverlap.length > 0) {
        const detail = checkData2.assigneeOverlap
          .map(
            (row) =>
              `${row.name}: ${row.entries.map((e) => `${e.meters}m (${e.source})`).join(', ')}`
          )
          .join('\n');
        needAck = window.confirm(
          `Some selected athletes already have personal meters for this date:\n\n${detail}\n\nOK to add this credit, or Cancel to go back.`
        );
        if (!needAck) return;
      }
      await postMeters(skipBoatTotal, needAck);
    } catch (err) {
      setQlError('Failed to log meters');
    } finally {
      setQlLoading(false);
    }
  };

  const myAthlete = athletes.find((a) =>
    (user?.athleteId && Number(a.id) === Number(user.athleteId))
    || (user?.athlete_id && Number(a.id) === Number(user.athlete_id))
    || (user?.id && Number(a.user_id) === Number(user.id))
  );
  const myAthleteName = myAthlete?.name;
  const hasAthleteProfile = Boolean(user?.athleteId || user?.athlete_id || myAthlete);

  const lineupBoatClass = (session) => {
    if (!myAthleteName) return null;
    for (const boat of session.boatings || []) {
      const inSeats = (boat.seats || []).some((s) => s.athlete === myAthleteName);
      const inList = (boat.athletes || []).includes(myAthleteName);
      if (inSeats || inList) return boat.boatClass || boat.boat_class || null;
    }
    return null;
  };

  return (
    <div className="p-2 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-xl sm:text-2xl font-bold">Daily Workout</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={openQuickReserve}
            className="px-3 py-2 rounded-lg text-sm font-semibold bg-white border-2 border-theme-primary text-theme-primary hover:bg-theme-primary/5"
          >
            Quick reserve
          </button>
          <button
            type="button"
            onClick={openQuickLog}
            className="px-3 py-2 rounded-lg text-sm font-semibold bg-theme-primary text-white hover:opacity-90"
          >
            Quick log
          </button>
        </div>
      </div>

      {/* Calendar View */}
      <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow-sm border">
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-theme-bg-secondary rounded-t-lg">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-center">
            <h3 className="text-lg sm:text-xl font-bold text-theme-primary">
              {calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </h3>
            <button
              onClick={goToToday}
              className="text-xs text-theme-primary hover:underline"
            >
              Go to Today
            </button>
          </div>
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 text-center text-xs sm:text-sm font-semibold text-gray-500 bg-gray-50">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {getCalendarDays().map((date, index) => {
            const dateStr = formatDateMMDDYYYY(date);
            const calendarBadge = getCalendarBadge(dateStr);
            const isCurrentMonth = date.getMonth() === calendarDate.getMonth();
            const isToday = date.toDateString() === todayDateObj.toDateString();
            const isSelected = dateStr === selectedDate;
            const isPast = date < todayDateObj && !isToday;
            
            return (
              <button
                key={index}
                onClick={() => loadWorkoutByDate(dateStr)}
                className={`
                  relative p-2 sm:p-3 min-h-[50px] sm:min-h-[70px] border-b border-r text-left
                  transition-colors focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-inset
                  ${!isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'hover:bg-theme-bg-secondary'}
                  ${isSelected ? 'bg-theme-primary/10 ring-2 ring-theme-primary ring-inset' : ''}
                  ${isToday && !isSelected ? 'bg-theme-bg-secondary' : ''}
                  ${isPast && isCurrentMonth && !isSelected ? 'bg-gray-50/50' : ''}
                `}
              >
                <span className={`
                  text-sm sm:text-base font-medium
                  ${isToday ? 'bg-theme-primary text-white rounded-full w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center' : ''}
                  ${isSelected && !isToday ? 'text-theme-primary font-bold' : ''}
                `}>
                  {date.getDate()}
                </span>
                
                {calendarBadge && isCurrentMonth && (
                  <div className="mt-1">
                    <span className={`
                      inline-flex items-center justify-center
                      text-xs font-bold rounded-full
                      min-w-[20px] h-5 px-1.5
                      ${calendarBadge.kind === 'plan'
                        ? (isSelected ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800')
                        : (isSelected ? 'bg-theme-primary text-white' : 'bg-theme-bg-secondary text-theme-primary')
                      }
                    `}>
                      {calendarBadge.count}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="p-3 border-t bg-gray-50 rounded-b-lg">
          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 bg-theme-primary rounded-full"></span>
              <span>Today</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 bg-theme-bg-secondary border rounded"></span>
              <span>Workout lineups</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 bg-amber-100 border border-amber-300 rounded"></span>
              <span>Planned training</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 bg-theme-primary/10 border-2 border-theme-primary rounded"></span>
              <span>Selected</span>
            </div>
          </div>
        </div>
      </div>

      <TrainingBlockAnalytics
        weeks={trainingBlockWeeks}
        focusWeekStart={focusWeekStart}
        zoneLoggingEnabled={planZoneLoggingEnabled}
      />

      {(weather || water) ? (
        <div className="bg-theme-bg-secondary border border-theme-primary/30 text-theme-primary p-3 sm:p-4 rounded mb-4 sm:mb-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <span className="text-3xl sm:text-4xl">{weather?.emoji || '🌊'}</span>
            <div className="flex-1">
              <div className="font-bold text-sm sm:text-base mb-2">
                Conditions for {selectedDate}
                {teamSettings?.weatherLocationName && (
                  <span className="font-normal text-gray-600 ml-2">
                    ({teamSettings.weatherLocationName})
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4 text-sm">
                {weather && (
                  <>
                    <div className="bg-white/50 rounded p-2">
                      <div className="text-xs text-gray-600 uppercase tracking-wide">Air Temp</div>
                      <div className="text-lg sm:text-xl font-bold">{Math.round(weather.temp)}°F</div>
                    </div>
                    <div className="bg-white/50 rounded p-2">
                      <div className="text-xs text-gray-600 uppercase tracking-wide">Feels Like</div>
                      <div className="text-lg sm:text-xl font-bold">{Math.round(weather.feelsLike)}°F</div>
                    </div>
                    <div className="bg-white/50 rounded p-2">
                      <div className="text-xs text-gray-600 uppercase tracking-wide">Weather</div>
                      <div className="text-sm sm:text-base font-semibold capitalize">{weather.description}</div>
                    </div>
                    <div className="bg-white/50 rounded p-2">
                      <div className="text-xs text-gray-600 uppercase tracking-wide">Wind</div>
                      <div className="text-sm sm:text-base font-semibold">
                        {Math.round(weather.windSpeed)} mph {weather.windDirection}
                      </div>
                    </div>
                  </>
                )}
                {water?.temperatureF != null && (
                  <div className="bg-white/50 rounded p-2">
                    <div className="text-xs text-gray-600 uppercase tracking-wide">Water Temp</div>
                    <div className="text-lg sm:text-xl font-bold">{Math.round(water.temperatureF)}°F</div>
                    <div className="text-xs text-gray-500">{water.temperatureC}°C</div>
                  </div>
                )}
                {water?.dischargeCfs != null && (
                  <div className="bg-white/50 rounded p-2">
                    <div className="text-xs text-gray-600 uppercase tracking-wide">Flow</div>
                    <div className="text-sm sm:text-base font-semibold">
                      {Math.round(water.dischargeCfs).toLocaleString()} cfs
                    </div>
                  </div>
                )}
              </div>
              {water && (
                <p className="text-xs text-gray-500 mt-2">
                  Water: USGS {water.siteId}
                  {water.siteName ? ` (${water.siteName})` : ''}
                  {water.kind === 'latest' && selectedDate !== today ? ' — latest gauge reading, not a forecast' : ''}
                  {water.kind === 'daily' ? ' — daily mean' : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : selectedDate && (
        <div className="bg-gray-100 border border-gray-300 text-gray-800 p-3 sm:p-4 rounded mb-4 sm:mb-6 text-sm sm:text-base">
          Conditions not available for this date.
        </div>
      )}

      {targetTimesEnabled && (
        <KorzoSelfProg athlete={myAthlete} className="mb-4 sm:mb-6" />
      )}

      {/* Display all sessions for the selected date */}
      {sessions.length > 0 ? (
        <div className="space-y-4 sm:space-y-6">
          {sessions.length > 1 && (
            <div className="bg-theme-bg-secondary text-theme-primary px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base">
              <span className="font-semibold">{sessions.length} sessions</span> for {selectedDate}
            </div>
          )}
          
          {sessions.map((session, index) => {
            const isMySquadWorkout = !!session.is_my_squad;
            const isOtherSquadWorkout = session.squad_id != null && !isMySquadWorkout;
            const walkOnCandidates = getAthletesAvailableToAdd(session);
            const walkOnMatches = filterWalkOnSearch(walkOnCandidates);
            const walkOnPickerOpen = walkOnPickerSessionId === session.id;
            return (
            <div
              key={session.id || index}
              className={`bg-white p-4 sm:p-6 rounded-lg shadow-md border-l-4 ${
                isMySquadWorkout
                  ? 'border-green-600 ring-1 ring-green-200'
                  : isOtherSquadWorkout
                    ? 'border-gray-300 opacity-80'
                    : 'border-theme-primary'
              }`}
            >
              {/* Session header */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                {isMySquadWorkout && (
                  <span className="bg-green-600 text-white text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full">
                    Your squad · {session.squad_name}
                  </span>
                )}
                {isOtherSquadWorkout && session.squad_name && (
                  <span className="bg-gray-200 text-gray-700 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full">
                    {session.squad_name}
                  </span>
                )}
                {sessions.length > 1 && (
                  <span className="bg-theme-primary text-white text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full">
                    {getSessionLabel(session.session_number, sessions.length) || `Session ${session.session_number}`}
                  </span>
                )}
                <h3 className="text-lg sm:text-2xl font-bold">{session.title}</h3>
              </div>
              
              {session.notes && (
                <p className="mb-4 text-gray-700 whitespace-pre-wrap text-sm sm:text-base">{session.notes}</p>
              )}

              {targetTimesEnabled && (
              <WorkoutTargetCard
                className="mb-4"
                goalPercent={session.goal_percent}
                targets={session.korzo_targets}
                boatClass={lineupBoatClass(session)}
                promptForGoal={hasAthleteProfile}
              />
              )}

              {session.boatings && session.boatings.length > 0 && (
                <>
                  <h4 className="text-base sm:text-lg font-semibold mb-2">Boatings:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {session.boatings.map((boat, idx) => {
                      const crewAverages = calculateCrewAverages(boat);
                      const hasSeatsFormat = boat.seats && Array.isArray(boat.seats);
                      
                      return (
                        <div key={idx} className="border p-3 sm:p-4 rounded bg-gray-50">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <h5 className="font-bold text-theme-primary text-sm sm:text-base">{boat.boatName}</h5>
                            {boat.oarName && (
                              <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                {boat.oarName}
                              </span>
                            )}
                          </div>
                          
                          {hasSeatsFormat ? (
                            // New format: seats array
                            <div className="mt-2 space-y-1">
                              {boat.seats.filter(s => s.athlete).map((seatData, i) => {
                                const isAbsent = absentAthletes.includes(seatData.athlete);
                                return (
                                  <div key={i} className={`flex items-center gap-2 text-sm ${isAbsent ? 'opacity-60' : ''}`}>
                                    <span className={`inline-block w-14 text-center text-xs font-semibold px-1.5 py-0.5 rounded ${getSeatLabelStyle(seatData.seat)}`}>
                                      {seatData.seat}
                                    </span>
                                    <span className={isAbsent ? 'text-red-500 line-through' : ''}>
                                      {seatData.athlete}
                                      {isAbsent && <span className="text-xs ml-1">(absent)</span>}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            // Old format: athletes array
                            <ul className="list-disc list-inside mt-2 text-sm">
                              {(boat.athletes || []).map((athlete, i) => {
                                const coxswains = boat.coxswains || [];
                                const isCoxswain = coxswains.includes(i);
                                const isAbsent = absentAthletes.includes(athlete);
                                return (
                                  <li key={i} className={isAbsent ? 'text-red-500 line-through' : ''}>
                                    {athlete}
                                    {isCoxswain && (
                                      <span className="ml-1 sm:ml-2 text-xs bg-purple-100 text-purple-700 px-1 sm:px-1.5 py-0.5 rounded">
                                        Cox
                                      </span>
                                    )}
                                    {isAbsent && <span className="text-xs ml-1">(absent)</span>}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          
                          {/* Crew Averages */}
                          {(boat.showAvgWeight || boat.showAvgErg) && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                                {boat.showAvgWeight && (
                                  <div className="bg-blue-50 px-2 sm:px-3 py-1 rounded">
                                    <span className="font-semibold text-blue-700">Avg Weight:</span>{' '}
                                    {crewAverages.avgWeight !== null ? (
                                      <span className="text-blue-900">
                                        {crewAverages.avgWeight} lbs
                                        {crewAverages.weightCount < crewAverages.totalCount && (
                                          <span className="text-blue-500 text-xs ml-1">
                                            ({crewAverages.weightCount}/{crewAverages.totalCount})
                                          </span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-gray-500">N/A</span>
                                    )}
                                  </div>
                                )}
                                {boat.showAvgErg && (
                                  <div className="bg-green-50 px-2 sm:px-3 py-1 rounded">
                                    <span className="font-semibold text-green-700">Avg 2k:</span>{' '}
                                    {crewAverages.avgErg !== null ? (
                                      <span className="text-green-900">
                                        {crewAverages.avgErg}
                                        {crewAverages.ergCount < crewAverages.totalCount && (
                                          <span className="text-green-500 text-xs ml-1">
                                            ({crewAverages.ergCount}/{crewAverages.totalCount})
                                          </span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-gray-500">N/A</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {crewAverages.coxCount > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  * Excludes {crewAverages.coxCount} cox
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Sign-Up Section */}
              {session.signup_enabled && session.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h4 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      Sign-Up
                    </h4>
                    {signupStatus[session.id] && (
                      <span className="text-sm text-gray-600">
                        {signupStatus[session.id].signups?.length || 0}
                        {session.signup_limit ? ` / ${session.signup_limit}` : ''} signed up
                      </span>
                    )}
                  </div>

                  {/* Sign-up button for athletes */}
                  {signupStatus[session.id] && (
                    <div className="space-y-3">
                      {!signupStatus[session.id].isSignedUp ? (
                        <button
                          onClick={() => handleSignUp(session.id)}
                          disabled={signingUp === session.id || (signupStatus[session.id].spotsRemaining !== null && signupStatus[session.id].spotsRemaining <= 0)}
                          className={`w-full sm:w-auto px-4 py-3 sm:py-2 rounded-lg font-medium transition-colors ${
                            signupStatus[session.id].spotsRemaining !== null && signupStatus[session.id].spotsRemaining <= 0
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {signingUp === session.id ? 'Signing up...' : 
                           signupStatus[session.id].spotsRemaining !== null && signupStatus[session.id].spotsRemaining <= 0 
                             ? 'Full' 
                             : '✓ Sign Up for This Workout'}
                        </button>
                      ) : (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                          <span className="inline-flex items-center gap-2 px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            You're signed up!
                          </span>
                          <button
                            onClick={() => handleCancelSignup(session.id)}
                            disabled={signingUp === session.id}
                            className="text-sm text-red-600 hover:text-red-800 hover:underline"
                          >
                            Cancel
                          </button>
                        </div>
                      )}

                      {/* List of signed-up athletes */}
                      {signupStatus[session.id].signups?.length > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-semibold mb-2">Signed up:</p>
                          <div className="flex flex-wrap gap-2">
                            {signupStatus[session.id].signups.map((signup) => (
                              <span 
                                key={signup.id}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-white border rounded text-sm"
                              >
                                {signup.athlete_name}
                                {signup.athlete_team && (
                                  <span className="text-xs text-gray-500">({signup.athlete_team})</span>
                                )}
                                {(isCoach() || isAdmin()) && (
                                  <button
                                    onClick={() => handleRemoveAthleteSignup(session.id, signup.athlete_id, signup.athlete_name)}
                                    className="ml-1 text-red-500 hover:text-red-700"
                                    title="Remove from workout"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Coach: add someone who showed up without signing up */}
                      {(isCoach() || isAdmin()) && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => toggleWalkOnPicker(session.id)}
                            className="w-full flex items-center justify-between gap-2 p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                          >
                            <span>Add an athlete who showed up</span>
                            <svg
                              className={`w-4 h-4 transition-transform ${walkOnPickerOpen ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {walkOnPickerOpen && (
                            <div className="mt-1 border rounded-lg bg-white overflow-hidden">
                              {walkOnCandidates.length === 0 ? (
                                <p className="p-3 text-sm text-gray-500">
                                  {getWalkOnEmptyMessage(session)}
                                </p>
                              ) : (
                                <>
                                  <input
                                    type="text"
                                    value={walkOnSearch}
                                    onChange={(e) => setWalkOnSearch(e.target.value)}
                                    placeholder={
                                      walkOnCandidates.length === 1
                                        ? 'Search 1 athlete'
                                        : `Search ${walkOnCandidates.length} athletes`
                                    }
                                    className="w-full border-b p-3 text-base focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    autoFocus
                                  />
                                  {walkOnMatches.length === 0 ? (
                                    <p className="p-3 text-sm text-gray-500">
                                      No athletes match &quot;{walkOnSearch.trim()}&quot;.
                                    </p>
                                  ) : (
                                    <ul className="max-h-56 overflow-y-auto divide-y">
                                      {walkOnMatches.map((athlete) => (
                                        <li key={athlete.id}>
                                          <button
                                            type="button"
                                            onClick={() => handleAddAthleteSignup(session.id, athlete)}
                                            disabled={addingWalkOn !== null}
                                            className="w-full flex items-center justify-between gap-2 p-3 text-left text-sm hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            <span>{athlete.name}</span>
                                            <span className="text-green-600 font-semibold">
                                              {addingWalkOn?.athleteId === athlete.id ? 'Adding...' : '+ Add'}
                                            </span>
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      ) : null}

      {planMaterials.length > 0 && (
        <div className={sessions.length > 0 || planSessions.length > 0 ? 'mt-6' : ''}>
          <button
            type="button"
            onClick={() => setShowPlanMaterials(true)}
            className="inline-flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-950 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-amber-100"
          >
            Associated materials
            <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full">{planMaterials.length}</span>
          </button>
        </div>
      )}

      <MobileModal
        isOpen={showPlanMaterials}
        onClose={() => setShowPlanMaterials(false)}
        title="Associated materials"
      >
        <p className="text-sm text-gray-600 mb-4">
          Reference sheets for this training block. They are not planned sessions, so they do not fill the training log or count toward compliance.
        </p>
        <ul className="space-y-4">
          {planMaterials.map((item) => (
            <li key={item.id} className="border border-amber-200 rounded-lg p-3 bg-amber-50/40">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h3 className="font-semibold text-gray-900">{item.label}</h3>
                <span className="text-xs uppercase tracking-wide text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                  {materialKindLabel(item.kind)}
                </span>
                <span className="text-xs text-gray-500">
                  {item.squadName ? item.squadName : 'Whole team'}
                </span>
              </div>
              {item.kind === 'photo' || item.kind === 'pdf' ? (
                <div>
                  {item.kind === 'photo' && (
                    <img src={item.url} alt={item.label} className="max-h-56 rounded border bg-white" />
                  )}
                  <div className="mt-2 flex flex-wrap gap-3 text-sm">
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-theme-primary font-semibold hover:underline"
                    >
                      Open
                    </a>
                    <button
                      type="button"
                      onClick={() => downloadPlanMaterial(item)}
                      className="text-theme-primary font-semibold hover:underline"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ) : (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-theme-primary font-semibold hover:underline break-all"
                >
                  {item.kind === 'video' ? 'Open video' : 'Open page'}
                </a>
              )}
            </li>
          ))}
        </ul>
      </MobileModal>

      {planSessions.length > 0 && (
        <div className={`space-y-3 ${sessions.length > 0 || planMaterials.length > 0 ? 'mt-6' : ''}`}>
          {sessions.length > 0 && (
            <p className="text-sm font-semibold text-amber-800">Planned training volume</p>
          )}
          {sessions.length === 0 && (
            <div className="bg-amber-50 text-amber-900 px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base border border-amber-200">
              No workout published for {selectedDate} yet. Here&apos;s the plan from your coach
              {planSessions.length > 1 ? <> (<span className="font-semibold">{planSessions.length} sessions</span>)</> : null}.
            </div>
          )}
          {planSessions.map((entry, index) => {
            const isMySquad = entry.squadId != null && mySquadId != null && entry.squadId === mySquadId;
            const isOtherSquad = entry.squadId != null && !isMySquad;
            return (
            <div
              key={entry.id || `plan-${index}`}
              className={`p-4 sm:p-6 rounded-lg shadow-sm border-l-4 ${
                isMySquad
                  ? 'bg-amber-50 border-amber-600 ring-1 ring-amber-300'
                  : isOtherSquad
                    ? 'bg-gray-50 border-gray-300 opacity-75'
                    : 'bg-amber-50 border-amber-500'
              }`}
            >
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                {isMySquad && (
                  <span className="bg-green-600 text-white text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full">
                    Your squad · {entry.squadName}
                  </span>
                )}
                {isOtherSquad && (
                  <span className="bg-gray-200 text-gray-700 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full">
                    {entry.squadName}
                  </span>
                )}
                {planSessions.length > 1 && (
                  <span className={`text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full ${
                    isOtherSquad ? 'bg-gray-400 text-white' : 'bg-amber-600 text-white'
                  }`}>
                    Session {entry.sessionNumber}
                  </span>
                )}
                {entry.startTime && (
                  <span className={`text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full ${
                    isOtherSquad ? 'bg-gray-200 text-gray-700' : 'bg-amber-900 text-white'
                  }`}>
                    {formatTimeOfDay(entry.startTime)}
                  </span>
                )}
                {entry.boatSize && (
                  <span
                    className={`text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full border ${
                      isOtherSquad ? 'border-gray-300 text-gray-600' : 'border-amber-600 text-amber-900'
                    }`}
                    title={entry.boatSize === 'SB' ? 'Small boat session' : 'Big boat session'}
                  >
                    {entry.boatSize === 'SB' ? 'Small boat' : 'Big boat'}
                  </span>
                )}
                <h3 className={`text-lg sm:text-xl font-bold ${isOtherSquad ? 'text-gray-700' : 'text-amber-950'}`}>
                  {entry.title || `${entry.modality} ${entry.plannedMinutes}'`}
                </h3>
              </div>
              <p className={`text-sm sm:text-base ${isOtherSquad ? 'text-gray-600' : 'text-amber-900'}`}>
                {entry.modality} · {entry.plannedMinutes} min
                {formatPlanZones(entry.plannedZones)}
                {entry.location ? ` · ${entry.location}` : ''}
              </p>
              {entry.notes && (
                <p className="mt-2 text-gray-700 whitespace-pre-wrap text-sm sm:text-base">{entry.notes}</p>
              )}
              {entry.coachComment && (
                <div className={`mt-3 border-l-2 pl-3 py-1 ${isOtherSquad ? 'border-gray-300' : 'border-amber-400'}`}>
                  <p className={`text-[11px] font-semibold uppercase tracking-wide ${isOtherSquad ? 'text-gray-500' : 'text-amber-700'}`}>
                    Coach comment
                  </p>
                  <p className="text-gray-700 whitespace-pre-wrap text-sm sm:text-base italic">{entry.coachComment}</p>
                </div>
              )}
              {(entry.modality === 'Erg' || entry.modality === 'Row') && targetTimesEnabled && (
                <WorkoutTargetCard
                  className="mt-3"
                  goalPercent={entry.goalPercent}
                  targets={entry.korzo_targets}
                  promptForGoal={hasAthleteProfile}
                />
              )}
              <p className={`mt-2 text-xs ${isOtherSquad ? 'text-gray-500' : 'text-amber-700'}`}>
                From the training plan. Boat lineups and announcements are posted separately in Workout Builder when ready.
              </p>
            </div>
            );
          })}
        </div>
      )}

      {sessions.length === 0 && planSessions.length === 0 && selectedDate && (
        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg text-center text-gray-600">
          <p className="text-base sm:text-lg">No workout posted for {selectedDate} yet.</p>
        </div>
      )}

      {!selectedDate && (
        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg text-center text-gray-600">
          <p className="text-base sm:text-lg">Select a date to view workouts.</p>
        </div>
      )}

      {absentAthletes.length > 0 && (
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 p-3 sm:p-4 mt-4 sm:mt-6 rounded">
          <h4 className="text-base sm:text-lg font-bold mb-2">
            {selectedDate && selectedDate === formatDateMMDDYYYY(new Date())
              ? "Today's absences"
              : `Absent on ${selectedDate}:`}
          </h4>
          <ul className="list-disc list-inside text-sm sm:text-base">
            {absentAthletes.map((athlete, idx) => (
              <li key={idx}>{athlete}</li>
            ))}
          </ul>
        </div>
      )}

      {/* My Sign-up History Section */}
      <div className="mt-6 border-t pt-6">
        <button
          onClick={() => {
            setShowSignupHistory(!showSignupHistory);
            if (!showSignupHistory && signupHistory.length === 0) {
              fetchSignupHistory();
            }
          }}
          className="flex items-center gap-2 text-theme-primary font-semibold hover:underline"
        >
          <svg 
            className={`w-5 h-5 transform transition-transform ${showSignupHistory ? 'rotate-90' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          My Sign-up History
          {signupHistory.length > 0 && (
            <span className="text-sm font-normal text-gray-500">({signupHistory.length} sessions)</span>
          )}
        </button>

        {showSignupHistory && (
          <div className="mt-4 space-y-4">
            {/* Date filters */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">From</label>
                  <input
                    type="date"
                    value={historyDateRange.startDate}
                    onChange={(e) => setHistoryDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    className="border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">To</label>
                  <input
                    type="date"
                    value={historyDateRange.endDate}
                    onChange={(e) => setHistoryDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    className="border rounded px-3 py-2 text-sm"
                  />
                </div>
                <button
                  onClick={() => {
                    const start = historyDateRange.startDate ? 
                      new Date(historyDateRange.startDate).toLocaleDateString('en-US') : '';
                    const end = historyDateRange.endDate ? 
                      new Date(historyDateRange.endDate).toLocaleDateString('en-US') : '';
                    fetchSignupHistory(start, end);
                  }}
                  className="bg-theme-primary text-white px-4 py-2 rounded text-sm hover:opacity-90"
                >
                  Filter
                </button>
                <button
                  onClick={() => {
                    setHistoryDateRange({ startDate: '', endDate: '' });
                    fetchSignupHistory();
                  }}
                  className="text-gray-600 px-3 py-2 text-sm hover:text-gray-800"
                >
                  Clear
                </button>
              </div>
            </div>

            {signupHistoryLoading ? (
              <div className="text-center py-4 text-gray-500">Loading history...</div>
            ) : signupHistory.length === 0 ? (
              <p className="text-gray-500 text-sm">No sign-up history found. Sign up for workouts with sign-up enabled to see them here.</p>
            ) : (
              <>
                {/* Summary */}
                <div className="bg-theme-bg-secondary rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-theme-primary">{signupHistory.length}</div>
                      <div className="text-xs text-gray-600">Total Sign-ups</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {signupHistory.filter(s => s.is_past).length}
                      </div>
                      <div className="text-xs text-gray-600">Completed</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {signupHistory.filter(s => !s.is_past).length}
                      </div>
                      <div className="text-xs text-gray-600">Upcoming</div>
                    </div>
                  </div>
                </div>

                {/* Sessions list */}
                <div className="space-y-2">
                  {signupHistory.map((signup) => (
                    <div 
                      key={signup.id}
                      className={`border rounded-lg p-3 bg-white ${
                        signup.is_past ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-blue-500'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              signup.is_past ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {signup.is_past ? 'Completed' : 'Upcoming'}
                            </span>
                            {signup.team && (
                              <span className="text-xs text-gray-500">{signup.team}</span>
                            )}
                          </div>
                          <h4 className="font-semibold text-theme-primary">{signup.title}</h4>
                          <p className="text-sm text-gray-600">
                            {signup.date} • Session {signup.session_number}
                          </p>
                          {signup.workout_notes && (
                            <p className="text-sm text-gray-500 mt-1 italic">{signup.workout_notes}</p>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          Signed up {new Date(signup.signed_up_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <MobileModal
        isOpen={showQuickReserve}
        onClose={() => {
          setShowQuickReserve(false);
          setQrError('');
        }}
        title="Quick reserve"
        maxWidth="max-w-lg"
      >
        <div className="p-4">
          {reservableQuickBoats.length === 0 && (
            <p className="text-sm text-gray-600 mb-3">
              No reservable boats loaded. Private boats are only listed if you are an owner.
            </p>
          )}
          <form onSubmit={handleQuickReserve} className="space-y-3">
            {qrError && <p className="text-sm text-red-600">{qrError}</p>}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Boat</label>
              <select
                value={qrBoatId}
                onChange={(e) => setQrBoatId(e.target.value)}
                className="border rounded w-full px-3 py-2 text-sm"
                required
              >
                <option value="">Select a boat</option>
                {reservableQuickBoats.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.boat_class})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date</label>
              <input
                type="date"
                value={qrDate}
                onChange={(e) => {
                  const v = e.target.value;
                  const today = getLocalISODate();
                  if (v && v < today) {
                    setQrError('Reservations are not available for past dates.');
                    setQrDate(today);
                    return;
                  }
                  setQrError('');
                  setQrDate(v);
                }}
                min={getLocalISODate()}
                className="border rounded w-full px-3 py-2 text-sm"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Start</label>
                <select
                  value={qrStart}
                  onChange={(e) => setQrStart(e.target.value)}
                  className="border rounded w-full px-3 py-2 text-sm"
                >
                  {RESERVATION_TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {formatReservationTimeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">End</label>
                <select
                  value={qrEnd}
                  onChange={(e) => setQrEnd(e.target.value)}
                  className="border rounded w-full px-3 py-2 text-sm"
                >
                  {RESERVATION_TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {formatReservationTimeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Purpose (optional)</label>
              <input
                type="text"
                value={qrPurpose}
                onChange={(e) => setQrPurpose(e.target.value)}
                className="border rounded w-full px-3 py-2 text-sm"
                placeholder="e.g. Steady state"
              />
            </div>
            {!isLaunchEquipment(qrSelectedBoat?.boat_class) && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">Crew (optional, meter auto-credit)</p>
              <p className="text-xs text-gray-500 mb-2">
                When you log meters later, these members can be credited automatically. Select at most {qrCrewCap || '—'}{' '}
                (seats in this boat).
              </p>
              <SearchableMemberChecklist
                members={quickTeamMembers}
                selectedIds={qrAssignedUserIds}
                onChange={setQrAssignedUserIds}
                maxSelected={qrCrewCap}
                itemClassName="flex items-center gap-2 text-xs cursor-pointer"
                className="max-h-32 overflow-y-auto border rounded p-2 space-y-1 bg-white"
              />
            </div>
            )}
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={qrLoading}
                className="flex-1 bg-theme-primary text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {qrLoading ? 'Saving…' : 'Reserve'}
              </button>
              <button
                type="button"
                onClick={() => setShowQuickReserve(false)}
                className="px-4 py-2 rounded-lg border text-sm"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-gray-500">Times are in 15-minute increments. Full options are on the Equipment page.</p>
          </form>
        </div>
      </MobileModal>

      <MobileModal
        isOpen={showQuickLog}
        onClose={() => {
          setShowQuickLog(false);
          setQlError('');
        }}
        title="Quick log meters"
        maxWidth="max-w-lg"
      >
        <div className="p-4">
          {loggableQuickBoats.length === 0 && (
            <p className="text-sm text-gray-600 mb-3">Loading boats… or none available.</p>
          )}
          <form onSubmit={handleQuickLog} className="space-y-3">
            {qlError && <p className="text-sm text-red-600">{qlError}</p>}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Boat / equipment</label>
              <select
                value={qlBoatId}
                onChange={(e) => setQlBoatId(e.target.value)}
                className="border rounded w-full px-3 py-2 text-sm"
                required
              >
                <option value="">Select</option>
                {loggableQuickBoats.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.boat_class})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Date</label>
              <input
                type="date"
                value={qlDate}
                onChange={(e) => {
                  const v = e.target.value;
                  const today = getLocalISODate();
                  if (v && v > today) {
                    setQlError('Meters can only be logged for today or earlier, not for future dates.');
                    setQlDate(today);
                    return;
                  }
                  setQlError('');
                  setQlDate(v);
                }}
                max={getLocalISODate()}
                className="border rounded w-full px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Meters</label>
              <input
                type="number"
                min={1}
                step={1}
                value={qlMeters}
                onChange={(e) => setQlMeters(e.target.value)}
                className="border rounded w-full px-3 py-2 text-sm"
                placeholder="e.g. 12000"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Notes (optional)</label>
              <textarea
                value={qlNotes}
                onChange={(e) => setQlNotes(e.target.value)}
                className="border rounded w-full px-3 py-2 text-sm"
                rows={2}
                placeholder="Optional notes"
              />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">Credited to (optional)</p>
              <p className="text-xs text-gray-500 mb-2">
                Club members to receive this distance on their personal meters. At most {qlCrewCap || '—'} for this
                shell. (Also runs overlap checks: training vs boat, etc.)
              </p>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1 bg-white">
                {quickTeamMembers.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={qlAssignedUserIds.includes(String(m.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (qlCrewCap > 0 && qlAssignedUserIds.length >= qlCrewCap) return;
                          setQlAssignedUserIds([...qlAssignedUserIds, String(m.id)]);
                        } else {
                          setQlAssignedUserIds(qlAssignedUserIds.filter((id) => id !== String(m.id)));
                        }
                      }}
                    />
                    {m.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={qlLoading}
                className="flex-1 bg-theme-primary text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                {qlLoading ? 'Saving…' : 'Log meters'}
              </button>
              <button
                type="button"
                onClick={() => setShowQuickLog(false)}
                className="px-4 py-2 rounded-lg border text-sm"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-gray-500">
              You&apos;ll be prompted if a hull log already exists or if credit overlaps other personal meters that day.
            </p>
          </form>
        </div>
      </MobileModal>
    </div>
  );
}
